/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import {
  UpdateExportTemplatePayload,
  useEditExportBoTemplate
} from '@utils/api/back-office/customer-bo/use-edit-export-bo-template.tsx';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { Params, useParams } from 'react-router-dom';
import defaultPrompt, {
  defaultSummarizationPrompt
} from '@utils/types/defaultPrompt.ts';
import { useGetExportBoTemplate } from '@utils/api/back-office/customer-bo/use-get-bo-export-template.tsx';
import { Template } from '@utils/types/organization/export-template.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { Upload } from 'lucide-react';

type Props = {
  editTemplateModal: boolean;
  setEditTemplateModal: Dispatch<SetStateAction<boolean>>;
  selectedTemplateId: number | undefined;
  reset: UseFormReset<Template>;
  errors: FieldErrors<Template>;
  control: Control<Template>;
  register: UseFormRegister<Template>;
  handleSubmit: UseFormHandleSubmit<Template, Template>;
  templateType: { value: string; label: string };
  setNewTemplates: Dispatch<SetStateAction<Template[]>>;
  setValue: UseFormSetValue<Template>;
  getValues: UseFormGetValues<Template>;
  setTemplateType: Dispatch<SetStateAction<{ value: string; label: string }>>;
  watch: UseFormWatch<Template>;
};

export default function UpdateNewTemplateForm({
  editTemplateModal,
  setEditTemplateModal,
  selectedTemplateId,
  handleSubmit,
  templateType,
  setNewTemplates,
  register,
  setTemplateType,
  control,
  errors,
  getValues,
  setValue,
  reset,
  watch
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();
  const formRef = useRef<HTMLFormElement>(null);

  const { data: selectedTemplate, isLoading: exportTemplateIsLoading } =
    useGetExportBoTemplate(Number(customerId), selectedTemplateId || 0, {
      enabled: Boolean(selectedTemplateId)
    });

  const { mutate: editTemplate, isLoading: editIsLoading } =
    useEditExportBoTemplate();

  const watchedDocx = watch('template_docx');
  const watchedExample = watch('template_example');

  const getDefaultPrompt = (type: string) =>
    type === 'SUMMARIZATION' ? defaultSummarizationPrompt : defaultPrompt;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: getColour('appSoft'),
          border: `1px solid ${getColour('hairline')}`,
          borderRadius: 16
        },
        cardHeader: {
          borderBottom: `1px solid ${getColour('hairline')}`
        },
        label: { color: getColour('normalText') },
        subtle: { color: getColour('icon') }
      }),
    [getColour]
  );

  useEffect(() => {
    if (selectedTemplate?.result) {
      const tpl = selectedTemplate.result;
      const templateTypeValue = tpl.type || templateType.value;

      reset({
        name: tpl.name || '',
        template_type: templateTypeValue,
        prompt: tpl.prompt || getDefaultPrompt(templateTypeValue),
        template_docx: tpl.template_docx || null,
        template_example: tpl.template_example || null
      });

      setTemplateType({
        value: templateTypeValue,
        label: templateTypeValue
      });
    }
    // keep deps as-is (don’t add templateType) to avoid extra resets while editing
  }, [selectedTemplate, reset, setTemplateType]);

  const handleUpdateInputChange = <K extends keyof UpdateExportTemplatePayload>(
    key: K,
    value: UpdateExportTemplatePayload[K]
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(key as any, value as any);
  };

  const onSubmit = () => {
    if (!selectedTemplateId || !customerId) return;

    const values = getValues();
    const payload: UpdateExportTemplatePayload = {
      name: values.name,
      template_type: templateType.value,
      organization_id: Number(customerId),
      prompt: values.prompt,
      template_docx: watchedDocx instanceof File ? watchedDocx : null,
      template_example: watchedExample instanceof File ? watchedExample : null
    };

    editTemplate(
      { customerId, templateId: selectedTemplateId, payload },
      {
        onSuccess: () => {
          toast.success(t('export-template-updated-successfully'));
          setEditTemplateModal(false);

          reset({
            prompt: getDefaultPrompt(templateType.value),
            name: '',
            type: templateType.value,
            template_docx: null,
            template_example: null
          });

          setNewTemplates([
            {
              prompt: getDefaultPrompt(templateType.value),
              name: '',
              type: templateType.value,
              template_docx: null,
              template_example: null
            }
          ]);
        },
        onError: (error: AxiosError<BaseError>) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error?.response?.data.detail ||
            error?.response?.data.error ||
            error?.response?.data.message ||
            t('failed-to update-the-export-template');
          toast.error(errorMessage);
        }
      }
    );
  };

  const typeOptions = [
    { value: 'EXPORT_TEMPLATE', label: 'EXPORT_TEMPLATE' },
    { value: 'SUMMARIZATION', label: 'SUMMARIZATION' }
  ];

  const getFileName = (url: string) => {
    const fileNameWithQuery = url.split('/').pop() || url;
    return decodeURIComponent(fileNameWithQuery.split('?')[0]);
  };

  const UploadBox = ({
    id,
    title,
    hint,
    tooltip,
    accept,
    displayName,
    extensionTag,
    onFile
  }: {
    id: string;
    title: string;
    hint: string;
    tooltip: string;
    accept: string;
    displayName: string;
    extensionTag: string;
    onFile: (file: File) => void;
  }) => {
    return (
      <div className={cn(css(styles.card), 'overflow-hidden')}>
        <div
          className={cn(
            css(styles.cardHeader),
            'px-4 py-3 flex items-start justify-between gap-3'
          )}
        >
          <div className="min-w-0">
            <p className={cn(css(styles.label), 'text-sm font-semibold')}>
              {title}
            </p>
            <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>{hint}</p>
          </div>
          <span
            className={cn(
              css(styles.subtle),
              'text-[11px] px-2 py-1 rounded-full border whitespace-nowrap'
            )}
            style={{ borderColor: getColour('hairline') }}
          >
            {extensionTag}
          </span>
        </div>

        <div className="p-4">
          <Tooltip label={tooltip}>
            <label
              htmlFor={id}
              className={cn(
                'group w-full rounded-xl border-2 border-dashed cursor-pointer transition-all',
                'px-4 py-4 flex items-center gap-3',
                'hover:opacity-90',
                'min-h-[72px]'
              )}
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('hairline')
              }}
            >
              <span
                className="h-10 w-10 rounded-xl border grid place-items-center flex-shrink-0"
                style={{ borderColor: getColour('hairline') }}
              >
                <Upload width={18} className="opacity-70" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{displayName}</p>
                <p className="text-xs opacity-60 truncate">
                  {t('click-to-upload')}
                </p>
              </div>

              <span
                className="text-xs font-medium px-3 py-2 rounded-2xl border whitespace-nowrap"
                style={{
                  borderColor: getColour('hairline'),
                  backgroundColor: getColour('appSoft')
                }}
              >
                {t('choose-file')}
              </span>

              <input
                id={id}
                type="file"
                accept={accept}
                className="sr-only"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  onFile(f);
                }}
              />
            </label>
          </Tooltip>
        </div>
      </div>
    );
  };

  const docxDisplayName =
    watchedDocx instanceof File
      ? watchedDocx.name
      : typeof selectedTemplate?.result.template_docx === 'string'
        ? getFileName(selectedTemplate.result.template_docx)
        : t('click-to-upload');

  const pdfDisplayName =
    watchedExample instanceof File
      ? watchedExample.name
      : typeof selectedTemplate?.result.template_example === 'string'
        ? getFileName(selectedTemplate.result.template_example)
        : t('click-to-upload');

  return (
    <Modal
      isOpen={editTemplateModal}
      onClose={() => {
        reset({
          prompt: getDefaultPrompt(templateType.value),
          name: '',
          type: templateType.value,
          template_docx: null,
          template_example: null
        });
        setEditTemplateModal(false);
      }}
      size="medium"
      title={t('update-template')}
      caption={t('update-the-templates-information-and-upload-the-new-files')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => {
            reset({
              prompt: getDefaultPrompt(templateType.value),
              name: '',
              type: templateType.value,
              template_docx: null,
              template_example: null
            });
            setEditTemplateModal(false);
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={editIsLoading}
        />,
        <Button
          key="update"
          onClick={() => {
            formRef.current?.requestSubmit();
          }}
          label={editIsLoading ? `${t('updating')}…` : t('update')}
          size="small"
          variant="primary"
          disabled={editIsLoading}
        />
      ]}
    >
      {exportTemplateIsLoading ? (
        <div
          className="text-sm text-center py-10"
          style={{ color: getColour('inactiveText') }}
        >
          {t('loading')}…
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          ref={formRef}
          className="space-y-5"
        >
          {/* Prompt (same card style as Create) */}
          <div className={cn(css(styles.card), 'p-4 md:p-5')}>
            <Input
              id="prompt"
              label={t('prompt')}
              type="text"
              multiline
              {...register('prompt')}
              defaultValue={
                selectedTemplate?.result.prompt ||
                getDefaultPrompt(
                  selectedTemplate?.result.type || templateType.value
                )
              }
              className="w-full text-md"
              error={errors.prompt?.message}
            />
          </div>

          {/* Name + Type (balanced, consistent spacing) */}
          <div className={cn(css(styles.card), 'p-4 md:p-5')}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-8">
                <Input
                  id="name"
                  label={t('name')}
                  type="text"
                  placeholder={t('template-name')}
                  autoComplete="name"
                  {...register('name')}
                  defaultValue={selectedTemplate?.result.name}
                  error={errors.name?.message}
                />
              </div>

              <div className="md:col-span-4">
                <label
                  htmlFor="template_type"
                  className="block text-sm font-medium mb-2"
                  style={{ color: getColour('normalText') }}
                >
                  {t('type')}
                </label>

                {/* keeps same Controller logic; wrapper helps align heights visually */}
                <div className="h-10">
                  <Controller
                    control={control}
                    name="template_type"
                    render={({ field: { onChange } }) => (
                      <SelectInput
                        id="template_type"
                        name="template_type"
                        options={typeOptions}
                        defaultValue={{
                          value: selectedTemplate?.result.type || '',
                          label: selectedTemplate?.result.type || ''
                        }}
                        onChange={opt => {
                          const newType = opt?.value || '';
                          onChange(newType);
                          setTemplateType(opt!);
                          const newPrompt = getDefaultPrompt(newType);
                          setValue('prompt', newPrompt);
                        }}
                        isMulti={false}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Uploads (clearly separated into two cards, consistent with Create) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UploadBox
              id="template_docx"
              title={t('template-docx')}
              hint={t('upload-template-docx')}
              tooltip={t('upload-template-docx')}
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              extensionTag=".docx"
              displayName={docxDisplayName}
              onFile={file => {
                if (!file.name.toLowerCase().endsWith('.docx')) {
                  toast.error(t('please-upload-a-docx-file'));
                  return;
                }
                handleUpdateInputChange('template_docx', file);
              }}
            />

            <UploadBox
              id="template_example"
              title={t('template-pdf')}
              hint={t('upload-template-example')}
              tooltip={t('upload-template-example')}
              accept="application/pdf,.pdf"
              extensionTag=".pdf"
              displayName={pdfDisplayName}
              onFile={file => {
                if (!file.name.toLowerCase().endsWith('.pdf')) {
                  toast.error(t('please-upload-a-pdf-file'));
                  return;
                }
                handleUpdateInputChange('template_example', file);
              }}
            />
          </div>
        </form>
      )}
    </Modal>
  );
}
